import * as React from "react";
import { ClickAwayListener, Theme } from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import CheckIcon from "@material-ui/icons/Check";
import _ from "lodash";
import classNames from "classnames";
import { Form } from "formsy-react";
import { IProductMediaAsset } from "constants/assets";
import { format } from "lib/globalize-util";
import TextField from "components/form/text-field";
import type { IScope } from "reducers/scope";
import { List, ListRowProps } from "react-virtualized/dist/commonjs/List";
import { InfiniteLoader } from "react-virtualized/dist/commonjs/InfiniteLoader";
import { AutoSizer } from "react-virtualized/dist/commonjs/AutoSizer";
import { IItemRecord } from "records/item/item";
import Image from "components/items/item/image";
import { useDebounceFunction } from "hooks";
import theme from "styles/theme";
import { DATATEST } from "constants/integration";
import { SEARCH_PRODUCTS_MAX_LENGTH } from "constants/product";
import { withFilteredProductsFetch } from "./with-filtered-products-fetch";
import { ICart } from "constants/cart";

// prettier-ignore
interface IProps {
    scope: IScope;
    cart: ICart;
    idsInCart?: string[];
    productsMap: { [id: string]: IItemRecord };
    productCollection: {
        results: string[];
        isFetching: boolean;
        hasMoreResults: boolean;
        nextPage: string;
    };
    onSelectProduct: (product: IItemRecord, first3DProduct?: IProductMediaAsset) => void;
    onFetch: (filters: { [key: string]: string | number | boolean }) => void;
    onFetchNextPage: (nextPage: string) => void;

    calculatePrices?: boolean;

    searchFields?: string;
    width?: string | number;
    dropdownHeight?: number;
    autoComplete?: string;
    placeholder?: string;
    closeOnSelect?: boolean;
    disabled?: boolean;
    verify3DProduct?: boolean;
}

const PAGE_SIZE = 50;

const ProductSearchComponent: React.FC<IProps> = (props) => {
    const {
        scope,
        idsInCart = [],
        searchFields = "__all",
        productsMap,
        width: wrapperWidth = "100%",
        dropdownHeight = 300,
        autoComplete = "on",
        placeholder,
        closeOnSelect,
        disabled = false,
        verify3DProduct = false,
        calculatePrices = true,
    } = props;
    const {
        isFetching,
        hasMoreResults = true,
        results: ids = [],
        nextPage: nextPageUrl = "",
    } = props.productCollection;
    const { onFetchNextPage, onSelectProduct, onFetch } = props;

    const classes = useStyles({ width: wrapperWidth, dropdownHeight });
    const styles = React.useMemo(
        () => ({
            input: {
                padding: "0 36px 0 16px",
                marginTop: "-1px",
            },
            hint: {
                padding: "0 0 0 16px",
                bottom: "1px",
                lineHeight: "40px",
                height: "40px",
            },
        }),
        []
    );

    const [search, setSearch] = React.useState("");
    const [isOpenDropdown, setIsOpenDropdown] = React.useState(false);

    React.useEffect(() => {
        if (isOpenDropdown) {
            onFetch({
                __search: search,
                __fields: searchFields,
                __limit: PAGE_SIZE,
                __populate: "__variant",
                __calculate_prices: calculatePrices,
            });
        }
    }, [search, isOpenDropdown, searchFields, calculatePrices, onFetch]);

    const handleTextFieldChange = React.useCallback((e: Event, value: string) => setSearch(value), []);
    const handleTextFieldChangeDebounced = useDebounceFunction(handleTextFieldChange, 500);

    const handleInputFocus = React.useCallback(() => setIsOpenDropdown(true), []);
    const handleClickAway = React.useCallback(() => setIsOpenDropdown(false), []);

    const get3DProducts = React.useCallback(
        (productMedia: IProductMediaAsset[]) => productMedia.filter((asset) => asset.threedium_id),
        []
    );

    const handleSelectProduct = React.useCallback(
        (product: IItemRecord, first3DProduct?: IProductMediaAsset) => {
            onSelectProduct(product, first3DProduct);
            if (closeOnSelect) {
                setIsOpenDropdown(false);
            }
        },
        [onSelectProduct, closeOnSelect]
    );

    const isRowLoaded = React.useCallback(({ index }) => !!ids[index], [ids]);

    const rowCount = React.useMemo(
        () => (hasMoreResults || isFetching ? ids.length + PAGE_SIZE : ids.length),
        [hasMoreResults, isFetching, ids]
    );

    const loadMoreRows = React.useCallback(() => {
        if (hasMoreResults && !isFetching && nextPageUrl) {
            onFetchNextPage(nextPageUrl);
        }
    }, [onFetchNextPage, hasMoreResults, isFetching, nextPageUrl]);

    const noContentRenderer = React.useCallback(
        () => <div className={classes.noContent}>{format("No products found")}</div>,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const rowRenderer = React.useCallback(
        (rowProps: ListRowProps) => (
            <ProductItem
                ids={ids}
                idsInCart={idsInCart}
                productsMap={productsMap}
                scope={scope}
                onClick={handleSelectProduct}
                get3DProducts={verify3DProduct && get3DProducts}
                {...rowProps}
            />
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ids, idsInCart]
    );

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Form className={classes.wrapper}>
                <TextField
                    onChange={handleTextFieldChangeDebounced}
                    onFocus={handleInputFocus}
                    hintText={placeholder || format("Search for products")}
                    defaultValue={search}
                    name="product-search"
                    fullWidth="190px"
                    inputStyle={styles.input}
                    hintStyle={styles.hint}
                    large={false}
                    allowWhitespace={true}
                    canClear={true}
                    autoFocus={false}
                    autoComplete={autoComplete}
                    disabled={disabled}
                    dataTest={DATATEST.GALLERY.SEARCH_INPUT}
                    maxLength={SEARCH_PRODUCTS_MAX_LENGTH}
                />
                {isOpenDropdown && (
                    <div className={classes.dropdownWrapper}>
                        <InfiniteLoader
                            isRowLoaded={isRowLoaded}
                            loadMoreRows={loadMoreRows as any}
                            rowCount={rowCount}
                        >
                            {({ onRowsRendered, registerChild }) => (
                                <AutoSizer disableHeight={true}>
                                    {({ width }) => (
                                        <List
                                            ref={registerChild}
                                            onRowsRendered={onRowsRendered}
                                            noRowsRenderer={noContentRenderer}
                                            rowRenderer={rowRenderer}
                                            width={width}
                                            height={dropdownHeight}
                                            rowCount={rowCount}
                                            rowHeight={100}
                                            className={classes.dropdown}
                                        />
                                    )}
                                </AutoSizer>
                            )}
                        </InfiniteLoader>
                    </div>
                )}
            </Form>
        </ClickAwayListener>
    );
};

// prettier-ignore
interface IProductItemProps extends ListRowProps {
    scope: IScope;
    ids: string[];
    idsInCart?: string[];
    productsMap: { [id: string]: IItemRecord };
    onClick: (product: IItemRecord, first3DProduct?: IProductMediaAsset) => void;
    get3DProducts?: (productMedia: IProductMediaAsset[]) => IProductMediaAsset[];

}

export const ProductItem: React.FC<IProductItemProps> = (props) => {
    const { index, style, ids, productsMap, idsInCart, scope, onClick, get3DProducts } = props;
    const currentId = _.get(ids, [index]);
    const product: IItemRecord = _.get(productsMap, currentId);
    const [first3DProduct] = get3DProducts ? get3DProducts(_.get(product, "__media", [])) : [];
    const isProductInCart = React.useMemo(() => idsInCart.includes(currentId), [idsInCart, currentId]);

    const classes = useProductStyles({});
    const imageClasses = useImageStyles();

    const handleClick = React.useCallback(() => {
        if (!get3DProducts) {
            return onClick(product);
        } else if (first3DProduct) {
            return onClick(product, first3DProduct);
        }
    }, [product, get3DProducts]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div style={{ ...style, backgroundColor: theme.palette.white }}>
            {!currentId && (
                <div className={classes.wrapper}>
                    <div className={classNames(classes.imageSkeleton, classes.skeleton)} />

                    <div className={classes.description}>
                        <div className={classNames(classes.skeleton, classes.h1Skeleton)} />
                        <div className={classNames(classes.skeleton, classes.textSkeleton)} />
                        <div className={classNames(classes.skeleton, classes.textSkeleton)} />
                    </div>
                </div>
            )}
            {currentId && (
                <div
                    data-test={DATATEST.GALLERY.ITEM_TILE}
                    className={classNames(classes.wrapper, { disabledItem: get3DProducts && !first3DProduct })}
                    onClick={handleClick}
                >
                    <div className={classes.image}>
                        {isProductInCart && (
                            <div className={classes.badgeWrapper}>
                                <div className={classes.badge}>
                                    <CheckIcon style={{ color: theme.palette.primary1Color, fontSize: 25 }} />
                                </div>
                            </div>
                        )}
                        <Image scope={scope} item={product} size={"T"} classes={imageClasses} />
                    </div>

                    <div className={classes.description}>
                        <h1 className={classes.h1}>{_.get(product, "name")}</h1>
                        <span className={classes.text}>{_.get(product, "style_number")}</span>
                        <span className={classes.text}>{_.get(product, "color")}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export const ProductSearch = withFilteredProductsFetch(ProductSearchComponent);

const useStyles = makeStyles<Theme, { width: string | number; dropdownHeight: number }>({
    wrapper: ({ width }) => ({
        width,
        position: "relative",
        "& .disabledItem": {
            opacity: 0.35,
            cursor: "not-allowed",
        },
    }),
    dropdownWrapper: ({ dropdownHeight }) => ({
        position: "absolute",
        width: "100%",
        height: dropdownHeight,
        top: "102%",
        zIndex: 11,
        backgroundColor: "white",
    }),
    dropdown: {
        marginTop: 5,
        boxShadow: "0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22)",
    },
    noContent: {
        padding: 15,
        fontSize: 14,
    },
});

const useProductStyles = makeStyles({
    wrapper: {
        padding: 10,
        display: "flex",
        borderBottom: "1px solid #e2e2e2",
        cursor: "pointer",
        backgroundColor: "white",
        maxHeight: "100px",
    },
    disabled: {
        cursor: "not-allowed",
    },
    image: {
        position: "relative",
        width: 50,
        "& img": {
            height: "80px",
            objectFit: "contain",
        },
    },
    description: {
        marginLeft: 10,
        flex: 1,
        display: "flex",
        flexDirection: "column",
    },
    badgeWrapper: {
        position: "absolute",
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    badge: {
        width: 25,
        height: 25,
        borderRadius: 4,
        backgroundColor: "rgba(255, 255, 255, .8)",
        border: `1px solid ${theme.palette.borderColor}`,
    },
    h1: {
        margin: 0,
        fontSize: 12,
        lineHeight: "20px",
        fontWeight: "bold",
        color: "#2d2d2d",
    },
    text: {
        margin: 0,
        fontSize: 12,
        lineHeight: "16px",
        color: "#585858",
    },
    imageSkeleton: {
        width: 50,
        height: 80,
    },
    skeleton: {
        backgroundColor: "rgba(226, 226, 226, 0.5)",
        margin: 2,
    },
    textSkeleton: {
        width: 80,
        height: 12,
    },
    h1Skeleton: {
        width: 100,
        height: 16,
    },
});

const useImageStyles = makeStyles({
    picture: {
        width: "100%",
        height: "100%",
    },
    image: {
        position: "static !important" as any,
        transform: "unset !important",
    },
});
